/**
 * SalespersonTeamBlock Component
 *
 * One sales team on the Salesperson Performance card: a team header, the
 * team's member rows (already sorted by the card's active sort), and a
 * subtotal row. Teams come from Configuration → Sales Teams — the same
 * assignments the Sales Funnel card and Sales Team Report use — so a
 * salesperson sits on the same team everywhere.
 *
 * A configured team with no production still renders, with honest zeros, so
 * managers can see their whole roster.
 */

import React from 'react';
import { UsersIcon } from '@heroicons/react/24/outline';
import { Salesperson, SalespersonTeam } from './types';
import { SalespersonRow } from './SalespersonRow';
import { formatCurrency, formatPerUnit } from './format';
import { UnitsBreakdown } from './UnitsBreakdown';

interface SalespersonTeamBlockProps {
  team: SalespersonTeam;
  rowKey: (salesperson: Salesperson) => string;
  isExpanded: (salesperson: Salesperson) => boolean;
  onToggle: (salesperson: Salesperson) => void;
}

export const SalespersonTeamBlock: React.FC<SalespersonTeamBlockProps> = ({
  team,
  rowKey,
  isExpanded,
  onToggle,
}) => {
  const isUnassigned = team.managerId === '';
  const memberCount = team.salespeople.length;

  return (
    <div>
      <div className="flex items-center justify-between px-3 py-1.5 bg-gray-100 border-b border-gray-200">
        <div className="flex items-center gap-2 min-w-0">
          <UsersIcon className={`h-4 w-4 flex-shrink-0 ${isUnassigned ? 'text-gray-400' : 'text-blue-500'}`} />
          <span
            className={`text-xs font-semibold uppercase tracking-wide truncate ${
              isUnassigned ? 'text-gray-500' : 'text-gray-700'
            }`}
          >
            {isUnassigned ? 'Unassigned' : `Team — ${team.managerName}`}
          </span>
        </div>
        <span className="text-xs text-gray-500 flex-shrink-0">
          {memberCount} salesperson{memberCount !== 1 ? 's' : ''}
        </span>
      </div>

      {memberCount === 0 ? (
        <div className="px-3 py-3 text-sm text-gray-400 italic">
          No sales recorded for this team yet.
        </div>
      ) : (
        team.salespeople.map((sp, index) => (
          <SalespersonRow
            key={rowKey(sp)}
            salesperson={sp}
            isExpanded={isExpanded(sp)}
            onToggle={() => onToggle(sp)}
            rank={index + 1}
          />
        ))
      )}

      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-t border-gray-200">
        {/* Spacers mirror the member row's chevron + rank columns so the
            subtotal label lines up under the salesperson names. */}
        <div className="flex items-center gap-3 min-w-0">
          <span className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span className="w-5 flex-shrink-0" aria-hidden="true" />
          <span className="text-sm font-semibold text-gray-700">
            {isUnassigned ? 'Subtotal' : 'Team Subtotal'}
          </span>
        </div>
        <div className="flex items-center gap-4 flex-shrink-0">
          <div className="text-right">
            <span className="text-base font-bold text-gray-900">
              {formatCurrency(team.subtotals.totalGross)}
            </span>
            <div className="text-xs text-gray-500">
              Avg/Unit: {formatPerUnit(team.subtotals.avgTotalGrossPerUnit)}
            </div>
          </div>
          <UnitsBreakdown
            newUnits={team.subtotals.newUnits}
            swapUnits={team.subtotals.swapUnits}
            usedUnits={team.subtotals.usedUnits}
            wholesaleUnits={team.subtotals.wholesaleUnits}
            totalUnits={team.subtotals.totalUnits}
            emphasis
          />
        </div>
      </div>
    </div>
  );
};
